import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { BrregOrgDto, useSearchBrregOrgsByNameQuery } from "../../../autogen/bff-api";
import { useBaseData } from "../../../common/useBaseData";

const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 5;
const DEBOUNCE_MS = 300;

interface Args {
  countryId: string | undefined;
  query: string;
  enabled: boolean;
}

interface Result {
  isNorwaySelected: boolean;
  matches: BrregOrgDto[];
  isFetching: boolean;
}

export function useBrregNameSearch({ countryId, query, enabled }: Args): Result {
  const baseData = useBaseData();
  const norwayCountryId = baseData.countries.find((c) => c.alpha3Code === "NOR")?.id;
  const isNorwaySelected = !!norwayCountryId && countryId === norwayCountryId;

  const [debouncedQuery, setDebouncedQuery] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebounced = useCallback(
    debounce((value: string) => setDebouncedQuery(value), DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    setDebounced(query.trim());
  }, [query, setDebounced]);

  const trimmed = debouncedQuery;
  const skip = !enabled || !isNorwaySelected || trimmed.length < MIN_QUERY_LENGTH;

  const { data, isFetching } = useSearchBrregOrgsByNameQuery(
    { query: trimmed, limit: RESULT_LIMIT },
    { skip }
  );

  return {
    isNorwaySelected,
    matches: skip ? [] : data?.orgs ?? [],
    isFetching: !skip && isFetching,
  };
}
